import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import { InputAdornment } from '@mui/material';
import AutocompleteBase, {
  AutocompleteBaseProps,
} from 'components/forms/fields/autocomplete-field/autocomplete-base/autocomplete-base.component';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import usePlacesAutocomplete from 'use-places-autocomplete';
import { useAddressFormContext } from '../../address-form.hooks';

export type StreetAddress1Props = {
  name: string;
} & Partial<AutocompleteBaseProps>;

const StreetAddress1Field = ({ name, ...rest }: StreetAddress1Props) => {
  const { t } = useTranslation();
  const {
    suggestions: { data },
    setValue: setPlacesValue,
  } = usePlacesAutocomplete({
    requestOptions: {},
    debounce: 750,
  });

  const {
    field: { onChange: onFieldChange, value, ...fieldRest },
    fieldState: { error },
  } = useController({ name });

  const {
    placeContext: { setPlace },
  } = useAddressFormContext();

  const onChange = (_e, value) => setPlace(value);

  const handleInputChange = (_e, newValue) => {
    setPlacesValue(newValue);
    onFieldChange(newValue);
  };

  return (
    <AutocompleteBase
      freeSolo
      options={data}
      data-testid="StreetAddress1Field"
      inputValue={value ?? ''}
      onInputChange={handleInputChange}
      getOptionLabel={option => option.structured_formatting.main_text}
      renderOption={(props, option) => (
        <li {...props} data-testid="Autocomplete.Option">
          {option.description}
        </li>
      )}
      popupIcon={null}
      label={t('forms.fields.streetAddress1.label')}
      noOptionsText={t('PlaceAutocomplete.noOptions')}
      TextFieldProps={{
        placeholder: t('PlaceAutocomplete.placeholder'),
      }}
      filterOptions={options => options}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <LocationOnOutlined />
          </InputAdornment>
        ),
      }}
      isOptionEqualToValue={(option, value) =>
        option.place_id === value.place_id
      }
      onChange={onChange}
      {...fieldRest}
      error={error}
      {...rest}
    />
  );
};

export default StreetAddress1Field;
